// La configuración del catálogo (ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa):
// formato y calidad (con los menús de Atlas: ADR-2026-10-15-formato-y-calidad-del-catalogo-como-en-atlas),
// estilo común y tomas, PLEGADOS en un contenedor que plegado sólo dice
// "Configuración del catálogo". Desplegado, se editan como antes (la X quita una toma; sumar o cambiar tomas es con el
// agente). El título va aparte, como encabezado de la pantalla (`TituloEditable`).
import { ChevronDown, Loader2, Pencil, Settings2, Wand2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GlifoDeProporcion } from '../../../../chat-atlas/ui/PastillasDeContenido';
import { creditosPorResolucion, labelResolucion, OPCIONES_RESOLUCION, type ResolucionImagen } from '../../../../lib/resolucionImagen';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { ICONO_DE_TOMA } from './iconosDeToma';
import { costoDelPlan, MAX_ESTILO, totalImagenes, type PlanDeCatalogo } from './plan';
import { SelectorDeOpciones } from './SelectorDeOpciones';

export interface ConfiguracionDelCatalogoProps {
  plan: PlanDeCatalogo;
  escribiendo: boolean;
  pendientesDeEscritura: number;
  /** `reescribir` = el texto cambió de verdad: los prompts no fijados vuelven a escribirse. */
  onEstilo: (e: string, reescribir?: boolean) => void;
  onFormato: (slug: string) => void;
  onResolucion: (r: ResolucionImagen) => void;
  onQuitarToma: (id: string) => void;
  onEscribir: () => void;
}

export const TituloEditable: React.FC<{ valor: string; onGuardar: (v: string) => void }> = ({ valor, onGuardar }) => {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(valor);
  useEffect(() => setBorrador(valor), [valor]);
  if (!editando) {
    return (
      <button
        type="button"
        onClick={() => setEditando(true)}
        className="group inline-flex max-w-full items-center gap-2 rounded-lg px-1 py-0.5 text-left hover:bg-black/[0.04]"
        title="Cambiar el nombre"
      >
        <span className="truncate text-[22px] font-bold leading-tight tracking-[-0.02em] text-brand-black">{valor}</span>
        <Pencil className="h-4 w-4 shrink-0 text-[#02062B]/[0.35] opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={2} aria-hidden="true" />
      </button>
    );
  }
  const guardar = () => {
    setEditando(false);
    if (borrador.trim() && borrador.trim() !== valor) onGuardar(borrador);
    else setBorrador(valor);
  };
  return (
    <input
      autoFocus
      value={borrador}
      onChange={(e) => setBorrador(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') guardar();
        if (e.key === 'Escape') {
          setBorrador(valor);
          setEditando(false);
        }
      }}
      maxLength={80}
      className="w-full max-w-[520px] rounded-lg border border-brand-primary px-2 py-0.5 text-[22px] font-bold leading-tight tracking-[-0.02em] text-brand-black outline-none"
    />
  );
};

export const ConfiguracionDelCatalogo: React.FC<ConfiguracionDelCatalogoProps> = ({
  plan,
  escribiendo,
  pendientesDeEscritura,
  onEstilo,
  onFormato,
  onResolucion,
  onQuitarToma,
  onEscribir,
}) => {
  const [abierta, setAbierta] = useState(false);
  const [estiloAbierto, setEstiloAbierto] = useState(false);
  // Borrador local: el estilo se confirma UNA vez al salir del campo. Si cada tecla fuera al plan,
  // al salir el plan ya tendría el texto nuevo y `setEstilo` no vería el cambio (review 2026-10-15).
  const [borradorEstilo, setBorradorEstilo] = useState('');

  return (
    <section className="rounded-[20px] bg-[#F5F6FA] p-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          aria-expanded={abierta}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-0.5 text-left"
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white text-brand-black shadow-sm">
            <Settings2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="shrink-0 text-[14.5px] font-semibold text-brand-black">Configuración del catálogo</span>
          <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-[#02062B]/[0.5] transition-transform ${abierta ? 'rotate-180' : ''}`} strokeWidth={2} aria-hidden="true" />
        </button>
        {pendientesDeEscritura > 0 && (
          <button
            type="button"
            onClick={onEscribir}
            disabled={escribiendo}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#E9EDFD] py-1.5 px-3 text-[13px] font-semibold text-brand-primary hover:bg-[#D8E0FB] disabled:opacity-60"
          >
            {escribiendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Wand2 className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />}
            {escribiendo ? 'Escribiendo…' : `Escribir ${pendientesDeEscritura} prompt${pendientesDeEscritura === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {abierta && (
        <div className="animate-fade-in mt-3 flex flex-col gap-3 rounded-lg bg-white p-3">
          {/* Formato y calidad con los menús de Atlas: qué es cada opción y lo que cuesta
              (ADR-2026-10-15-formato-y-calidad-del-catalogo-como-en-atlas). */}
          <div className="flex flex-wrap items-center gap-2">
            <SelectorDeOpciones
              rotulo="Formato"
              valor={FORMAT_OPTIONS_V2.find((f) => f.id === plan.formato)?.name ?? plan.formato}
              opciones={FORMAT_OPTIONS_V2.map((f) => ({
                id: f.id,
                etiqueta: f.name,
                glifo: (activo: boolean) => <GlifoDeProporcion etiqueta={f.name} activo={activo} />,
              }))}
              elegido={plan.formato}
              onElegir={onFormato}
              pie="El formato no cambia lo que cuesta. Vale para todas las fotos del catálogo."
            />
            <SelectorDeOpciones<ResolucionImagen>
              rotulo="Calidad"
              valor={OPCIONES_RESOLUCION.find((o) => o.id === plan.resolucion)?.label ?? plan.resolucion}
              opciones={OPCIONES_RESOLUCION.map((o) => {
                const n = creditosPorResolucion(o.id);
                return { id: o.id, etiqueta: o.label, detalle: o.detalle, costo: `${n} crédito${n === 1 ? '' : 's'} por imagen` };
              })}
              elegido={plan.resolucion}
              onElegir={onResolucion}
              pie={
                totalImagenes(plan) > 0
                  ? `Este catálogo son ${totalImagenes(plan)} imágenes: ${costoDelPlan(plan)} créditos en ${labelResolucion(plan.resolucion)}. Lo ya generado no se vuelve a cobrar.`
                  : undefined
              }
            />
          </div>

          <div className="flex items-start gap-2">
            <span className="shrink-0 pt-1 text-[13px] font-semibold text-[#3C3C43]/60">Estilo</span>
            {estiloAbierto ? (
              <textarea
                autoFocus
                value={borradorEstilo}
                onChange={(e) => setBorradorEstilo(e.target.value)}
                onBlur={() => {
                  setEstiloAbierto(false);
                  if (borradorEstilo.trim() !== plan.estilo.trim()) onEstilo(borradorEstilo, true);
                }}
                rows={3}
                maxLength={MAX_ESTILO}
                placeholder="Luz, paleta, ambiente, cámara: una decisión para todo el catálogo."
                className="flex-1 resize-y rounded-lg border border-brand-primary px-2.5 py-1.5 text-[13.5px] text-brand-black outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  setBorradorEstilo(plan.estilo);
                  setEstiloAbierto(true);
                }}
                className="min-w-0 flex-1 rounded-lg px-2 py-1 text-left text-[13.5px] leading-relaxed text-brand-black hover:bg-black/[0.04]"
                title="Editar el estilo común"
              >
                {plan.estilo.trim() || <span className="text-[#3C3C43]/50">Sin estilo común todavía: el agente lo propone, o escribilo acá.</span>}
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-[#3C3C43]/60">Tomas</span>
            {plan.tomas.length === 0 && <span className="text-[13px] text-[#3C3C43]/50">Ninguna todavía.</span>}
            {plan.tomas.map((t) => {
              const Icono = ICONO_DE_TOMA[t.icono];
              return (
                <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F3F9] py-1.5 px-3 text-[13px] font-semibold text-brand-black" title={t.queMuestra}>
                  <Icono className="h-3.5 w-3.5 text-[#02062B]/[0.6]" strokeWidth={2.25} aria-hidden="true" />
                  {t.titulo}
                  {t.cantidad > 1 && <span className="text-[#3C3C43]/60">×{t.cantidad}</span>}
                  <button type="button" onClick={() => onQuitarToma(t.id)} aria-label={`Quitar la toma ${t.titulo}`} className="ml-0.5 text-[#02062B]/[0.4] hover:text-[#B42318]">
                    <X className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};
